/**
 * The live half of multiplayer: one `NetClient`, its retry policy, the
 * clipboard, and the translation from "what the connection is doing" into the
 * `NetView` the menu draws.
 *
 * The same shape as ./training's `TrainingRuntime`, and for the same reason:
 * `MenuModel` must stay constructible in Node with no socket, no clipboard and
 * no timers, so it cannot own a connection. It owns a MIRROR of one, written
 * from here.
 *
 * ===========================================================================
 * RETRYING IS A POLICY, NOT A PROTOCOL FEATURE
 * ===========================================================================
 *
 * `NetClient` is clock-free by construction — it never sets a timer and never
 * reads one it was not handed — which is what lets the whole netcode suite run
 * without waiting. So the "try three times, then let the player decide" rule
 * lives here, where there is a real `setTimeout`, and it is injectable so a
 * test can drive it by hand.
 *
 * The backoff is 1 s, 2 s, 4 s — FIXED, not jittered. There is no thundering
 * herd to spread here (one player, one socket), and `Math.random` in anything
 * reproducible is a rule this tree keeps.
 *
 * Three attempts and then a button, rather than retrying forever: a client that
 * silently reconnects for two minutes has spent the server's entire reconnect
 * grace and the seat is gone anyway, and a screen that has been saying
 * "reconnecting" for two minutes has stopped being information.
 */

import type { GameSettings } from '../game/settings';
import { sanitizeRoomCode } from '../game/settings';
import { modeRules } from '../net/modes';
import type { RoomState, Transport } from '../net/protocol';
import {
  NetClient,
  type NetFailure,
  type NetIntent,
  type ReconnectTicket,
} from '../net/client';
import { WebSocketTransport, normalizeServerUrl, pageIsSecure } from '../net/ws-transport';
import type { MenuNetIntent, NetUiEvent } from './menu-model';
import { idleNetView, netStatusLine, type NetPlayerRow, type NetView } from './net-model';

/** Milliseconds before each automatic reconnect attempt. */
export const RETRY_DELAYS_MS: readonly number[] = [1000, 2000, 4000];

export interface NetRuntimeHooks {
  /** The mirrored view moved; re-read `view` and re-render. */
  changed(): void;
  /** A discrete moment the menu should navigate on. */
  navigate(event: NetUiEvent): void;
  /** The predicted world exists; build the render session around `client`. */
  matchStarted(client: NetClient): void;
  /** Whatever was built for `matchStarted` must be torn down. */
  matchEnded(): void;
}

/** An opened connection, and the way to shut it. */
export interface OpenedTransport {
  transport: Transport;
  close(): void;
}

export interface NetRuntimeOptions {
  /** Builds a transport. Injected so a test needs no socket. */
  connect?: (url: string) => OpenedTransport;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  /** Writes to the clipboard, or null when there is none. */
  clipboard?: ((text: string) => void) | null;
  /**
   * Monotonic milliseconds for the client's round-trip estimate. This is the
   * layer that owns the browser's clock — src/net deliberately reads none, so
   * the netcode stays importable by the dedicated server. See `NetClientOptions.now`.
   */
  now?: () => number;
  /**
   * The login token to present in Hello, read per dial so a sign-in that
   * happened after boot is picked up. Null (the default) sends none — a server
   * with accounts enabled will kick with `auth`, which is the honest outcome
   * for a connection attempted while signed out.
   */
  authToken?: () => string | null;
}

/** Failures that trying again cannot fix. Offering a Reconnect row for one of
 *  these would be a button whose only function is to fail identically. */
const TERMINAL: readonly NetFailure[] = [
  'protocol-version',
  'room-full',
  'no-such-room',
  'bad-request',
  // Retrying without signing in fails identically; the fix is on the account
  // screen, not on this one.
  'auth',
];

export class NetRuntime {
  private client: NetClient | null = null;
  private state: NetView = idleNetView();
  private intent: MenuNetIntent | null = null;
  private ticket: ReconnectTicket | null = null;
  private retryHandle: unknown = null;
  private retry = 0;
  private sessionLive = false;

  private readonly clipboard: ((text: string) => void) | null;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;
  private readonly open: (url: string) => OpenedTransport;
  private readonly now: () => number;
  private readonly authToken: () => string | null;

  constructor(
    private readonly settings: GameSettings,
    private readonly hooks: NetRuntimeHooks,
    options: NetRuntimeOptions = {}
  ) {
    this.clipboard = options.clipboard === undefined ? browserClipboard() : options.clipboard;
    this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = options.clearTimer ?? (handle => clearTimeout(handle as number));
    this.open = options.connect ?? defaultConnect;
    this.now = options.now ?? (() => performance.now());
    this.authToken = options.authToken ?? (() => null);
  }

  get view(): NetView {
    return this.state;
  }

  get active(): NetClient | null {
    return this.client;
  }

  // -------------------------------------------------------------------------
  // Connecting
  // -------------------------------------------------------------------------

  /**
   * Open a session for `intent`.
   *
   * Any session already running is dropped first, deliberately and without
   * ceremony: two live `NetClient`s would both be predicting, both sending
   * inputs, and the server would see two players where there is one.
   */
  connect(intent: MenuNetIntent): void {
    this.disconnect();
    this.intent = intent;
    this.retry = 0;
    this.dial(intent, null);
  }

  /** Claim the seat the server is still holding, if it still is. */
  reconnect(): void {
    const ticket = this.ticket;
    const intent = this.intent;
    if (ticket === null || intent === null) return;
    this.dropClient();
    this.dial({ ...intent, code: ticket.code }, ticket);
  }

  private dial(intent: MenuNetIntent, ticket: ReconnectTicket | null): void {
    const url = normalizeServerUrl(this.settings.multiplayer.serverAddress, pageIsSecure());
    if (url === null) {
      this.state = {
        ...this.state,
        phase: 'closed',
        failure: 'refused',
        message: 'That server address is not usable. Set one on the Multiplayer screen.',
        canReconnect: false,
        retry: 0,
      };
      this.hooks.changed();
      this.hooks.navigate('closed');
      return;
    }

    const net: NetIntent = { mode: intent.mode, code: intent.code, create: intent.create };
    const opened = this.open(url);
    this.closeTransport = opened.close;

    const client = new NetClient({
      transport: opened.transport,
      displayName: this.settings.multiplayer.displayName,
      // The body chosen in match setup, so one car preference covers both games.
      // The server may still override it for a slot; the roster is the truth.
      car: this.settings.setup.car,
      intent: net,
      now: this.now,
      ticket,
      authToken: this.authToken(),
      listener: {
        onPhase: () => this.sync(),
        onRoomState: state => this.onRoom(state),
        onQueue: () => {
          this.sync();
          this.hooks.navigate('queued');
        },
        onMatchStart: () => this.onMatchStart(),
        onClosed: (failure, message) => this.onClosed(failure, message),
      },
    });
    this.client = client;
    client.start();
    this.sync();
  }

  private closeTransport: (() => void) | null = null;

  // -------------------------------------------------------------------------
  // Commands from the menu
  // -------------------------------------------------------------------------

  /** Send the ready flag opposite to the one the server last reported. */
  toggleReady(): void {
    const client = this.client;
    if (client === null) return;
    client.setReady(!(client.localPlayer?.ready ?? false));
  }

  switchTeam(): void {
    this.client?.switchTeam();
  }

  copyCode(): void {
    const code = this.state.code;
    if (code === null || this.clipboard === null) return;
    this.clipboard(code);
  }

  /** Leave on purpose. No failure, no retry, no disconnect screen. */
  leave(): void {
    this.client?.leave();
    this.disconnect();
  }

  /** Abandon whatever is in flight, quietly. */
  disconnect(): void {
    this.cancelRetry();
    this.dropClient();
    this.ticket = null;
    this.retry = 0;
    this.state = idleNetView();
    this.endSession();
    this.hooks.changed();
  }

  dispose(): void {
    this.cancelRetry();
    this.dropClient();
  }

  private dropClient(): void {
    const client = this.client;
    this.client = null;
    client?.dispose();
    const close = this.closeTransport;
    this.closeTransport = null;
    close?.();
  }

  // -------------------------------------------------------------------------
  // Client callbacks
  // -------------------------------------------------------------------------

  private onRoom(state: RoomState): void {
    // Remembering the code here rather than on Welcome is what makes Rejoin work
    // for a room the player was matched into as well as one they typed into.
    const code = sanitizeRoomCode(state.code);
    if (code.length > 0 && this.settings.multiplayer.lastRoomCode !== code) {
      this.settings.multiplayer.lastRoomCode = code;
    }
    this.ticket = this.client?.reconnectTicket ?? this.ticket;
    this.sync();
    if (!state.inProgress) this.hooks.navigate('lobby');
  }

  private onMatchStart(): void {
    const client = this.client;
    if (client === null) return;
    this.retry = 0;
    this.sessionLive = true;
    this.sync();
    this.hooks.matchStarted(client);
    this.hooks.navigate('started');
  }

  /**
   * The session ended.
   *
   * A DROP is the only failure worth retrying: everything else is a decision
   * the server made (full, closed, wrong version) and repeating the request
   * repeats the answer. Even a drop only retries while there is a seat to
   * reclaim — without a ticket there is nothing a reconnect could take.
   */
  private onClosed(failure: NetFailure | null, message: string): void {
    this.endSession();
    this.sync(failure, message);

    if (failure === null) return;
    if (failure === 'lost' && this.ticket !== null && this.retry < RETRY_DELAYS_MS.length) {
      this.scheduleRetry();
      return;
    }
    this.hooks.navigate('closed');
  }

  private scheduleRetry(): void {
    const delay = RETRY_DELAYS_MS[this.retry] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
    this.retry++;
    this.state = { ...this.state, phase: 'connecting', retry: this.retry, message: '' };
    this.state = { ...this.state, message: netStatusLine(this.state) };
    this.hooks.changed();
    this.retryHandle = this.setTimer(() => {
      this.retryHandle = null;
      this.reconnect();
    }, delay);
  }

  private cancelRetry(): void {
    if (this.retryHandle === null) return;
    this.clearTimer(this.retryHandle);
    this.retryHandle = null;
  }

  private endSession(): void {
    if (!this.sessionLive) return;
    this.sessionLive = false;
    this.hooks.matchEnded();
  }

  // -------------------------------------------------------------------------
  // The mirror
  // -------------------------------------------------------------------------

  /** Rebuild `view` from the client, and tell the menu to re-render. */
  sync(failure?: NetFailure | null, message?: string): void {
    this.state = this.snapshotView(failure, message);
    this.hooks.changed();
  }

  private snapshotView(failure?: NetFailure | null, message?: string): NetView {
    const client = this.client;
    if (client === null) {
      const base = idleNetView();
      return message === undefined
        ? base
        : { ...base, phase: 'closed', failure: failure ?? null, message };
    }

    const room = client.roomState;
    const localId = client.playerId;
    const players: NetPlayerRow[] = (room?.players ?? []).map(p => ({
      playerId: p.playerId,
      name: p.displayName,
      team: p.team,
      carIndex: p.carIndex,
      car: p.car,
      ready: p.ready,
      isBot: p.isBot,
      ping: p.ping,
      isLocal: p.playerId === localId,
    }));

    const mode = room?.mode ?? this.intent?.mode ?? 'duel';
    const view: NetView = {
      phase: client.phase,
      failure: failure ?? client.failure,
      message: message ?? client.failureDetail,
      mode,
      code: client.roomCode,
      teamSize: room?.teamSize ?? modeRules(mode).spec.teamSize,
      teams: modeRules(mode).teams,
      inProgress: room?.inProgress === true,
      players,
      local: players.find(p => p.isLocal) ?? null,
      ping: client.rttMs,
      degraded: client.degraded,
      retry: this.retry,
      retryLimit: RETRY_DELAYS_MS.length,
      // A seat is only reclaimable when the server issued a token for it AND
      // the failure is one a second attempt could survive.
      canReconnect:
        this.ticket !== null &&
        client.failure !== null &&
        !TERMINAL.includes(client.failure),
      canCopy: this.clipboard !== null,
      queue: client.queueStatus,
    };
    // `netStatusLine` is the single definition of what each phase reads as, so
    // the connecting screen, the lobby subtitle and the disconnect message
    // cannot describe the same state three different ways.
    return { ...view, message: view.phase === 'closed' ? view.message : netStatusLine(view) };
  }
}

// ---------------------------------------------------------------------------
// Browser wiring
// ---------------------------------------------------------------------------

const defaultConnect = (url: string): OpenedTransport => {
  const transport = new WebSocketTransport(url);
  return { transport, close: () => transport.close('replaced') };
};

/**
 * `navigator.clipboard.writeText`, or null.
 *
 * Null is a real answer, not a fallback: the API is absent on http origins
 * other than localhost and behind some permission policies, and the lobby's
 * copy row is DISABLED when it is — with the code spelled out in the note
 * instead. A copy button that silently does nothing is exactly the failure the
 * `enabled`/`note` convention exists to prevent.
 */
export const browserClipboard = (): ((text: string) => void) | null => {
  try {
    const nav = globalThis.navigator as { clipboard?: { writeText?: (t: string) => Promise<void> } };
    const write = nav?.clipboard?.writeText;
    if (typeof write !== 'function') return null;
    return (text: string) => {
      void nav.clipboard?.writeText?.(text)?.catch(() => {});
    };
  } catch {
    return null;
  }
};
